<script setup lang="ts">
import { ref } from "vue";
import { useAcl } from "vue-simple-acl";
import type LxcTabsWithNavigation from "~/core/components/TabsWithNavigation.vue";
import type { TabNavigation } from "~/core/components/TabsWithNavigation.vue";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import LxcError from "~/core/models/LxcError.class";
import BusinessUnitLogs from "~/modules/businessUnit/components/BusinessUnitLogs.vue";
import {
  PATH_BUSINESS_UNITS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_DESCRIPTION,
  PATH_BUSINESS_UNITS_ID_SETTINGS_FIRMWARES,
  PATH_BUSINESS_UNITS_ID_SETTINGS_LOGS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_RELATIONS,
  PATH_BUSINESS_UNITS_ID_SETTINGS_USERS,
} from "~/modules/businessUnit/constants";
import type { BusinessUnit } from "~/modules/businessUnit/models/BusinessUnit.interface";
import businessUnitService from "~/modules/businessUnit/services/businessUnit.service";
import { useConfirmLeavePageStore } from "~/stores/useConfirmLeavePageStore";
import ILxcPlus from "~icons/lxc/plus";

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const { can } = useAcl();
const confirmLeavePageStore = useConfirmLeavePageStore();

const businessUnitCode: Ref<number> = ref(
  parseInt(route.params.id.toString(), 10),
);
const isLoading: Ref<boolean> = ref(false);
const error: Ref<LxcError | null> = ref(null);
const businessUnit: Ref<BusinessUnit | undefined> = ref(undefined);

const showFirmwareForm = ref(false);

/**
 * Tabs configuration
 */
const tabsWithNavigationRef: Ref<typeof LxcTabsWithNavigation | undefined> =
  ref();

const descriptionTabId = "description";
const relationsTabId = "relations";
const usersTabId = "users";
const firmwaresTabId = "firmwares";
const logsTabId = "logs";

const usersTabRedirectPath = router.resolve({
  name: PATH_BUSINESS_UNITS_ID_SETTINGS_USERS,
  params: { id: businessUnitCode.value },
}).href;

const descriptionTabRedirectPath = router.resolve({
  name: PATH_BUSINESS_UNITS_ID_SETTINGS_DESCRIPTION,
  params: { id: businessUnitCode.value },
}).href;

const relationsTabRedirectPath = router.resolve({
  name: PATH_BUSINESS_UNITS_ID_SETTINGS_RELATIONS,
  params: { id: businessUnitCode.value },
}).href;

const firmwaresTabRedirectPath = router.resolve({
  name: PATH_BUSINESS_UNITS_ID_SETTINGS_FIRMWARES,
  params: { id: businessUnitCode.value },
}).href;

const logsTabRedirectPath = router.resolve({
  name: PATH_BUSINESS_UNITS_ID_SETTINGS_LOGS,
  params: { id: businessUnitCode.value },
}).href;

const selectedTabIndex: Ref<number | undefined> = ref();
const isUsersTabSelected: ComputedRef<boolean> = computed(
  (): boolean =>
    selectedTabIndex.value ===
    tabNavigations.findIndex(
      (tabNavigation: TabNavigation) => tabNavigation.id === usersTabId,
    ),
);
const showUserAdd: ComputedRef<boolean> = computed((): boolean => {
  return isUsersTabSelected.value && can(ACLRoles.CAN_WRITE_USER);
});
const isFirmwaresTabSelected: ComputedRef<boolean> = computed(
  (): boolean =>
    selectedTabIndex.value ===
    tabNavigations.findIndex(
      (tabNavigation: TabNavigation) => tabNavigation.id === firmwaresTabId,
    ),
);
const showFirmwareAdd: ComputedRef<boolean> = computed((): boolean => {
  return isFirmwaresTabSelected.value && can(ACLRoles.CAN_WRITE_FEATURE);
});

const tabNavigations: TabNavigation[] = [
  {
    id: descriptionTabId,
    redirectPath: descriptionTabRedirectPath,
    labelTranslationKey:
      "administration.businessUnit.settings.description.title",
  },
];

if (can(ACLRoles.CAN_READ_RELATION)) {
  tabNavigations.push({
    id: relationsTabId,
    redirectPath: relationsTabRedirectPath,
    labelTranslationKey: "administration.relations.title",
    forceRefresh: true,
  });
}

if (can(ACLRoles.CAN_READ_USER)) {
  tabNavigations.push({
    id: usersTabId,
    redirectPath: usersTabRedirectPath,
    labelTranslationKey: "administration.businessUnit.settings.users.title",
    forceRefresh: true,
  });
}

tabNavigations.push({
  id: logsTabId,
  redirectPath: logsTabRedirectPath,
  labelTranslationKey: "administration.businessUnit.settings.logs.title",
  forceRefresh: true,
});

if (can(ACLRoles.CAN_READ_FEATURE)) {
  tabNavigations.push({
    id: firmwaresTabId,
    redirectPath: firmwaresTabRedirectPath,
    labelTranslationKey: "administration.businessUnit.settings.firmwares.title",
    forceRefresh: true,
  });
}
/**
 * End tabs configuration
 */

const showUserForm: Ref<boolean> = ref(false);
const currentSelectedUser: Ref<string | undefined> = ref(undefined);

async function fetchData() {
  isLoading.value = true;

  const response = await businessUnitService.getBusinessUnit(
    businessUnitCode.value,
  );

  if (LxcError.check(response)) {
    error.value = response;
  } else {
    businessUnit.value = response;
  }

  isLoading.value = false;
}

function initLeavePageStore() {
  const excludedRoutes: string[] = tabNavigations.map((tab) => {
    return tab.redirectPath;
  });
  confirmLeavePageStore.setExcludedRoutes(excludedRoutes);
  confirmLeavePageStore.setTitle("administration.businessUnit.leave.title");
  confirmLeavePageStore.setConfirmMessage(
    "administration.organization.leave.message",
  );
}

function onAddUser() {
  currentSelectedUser.value = undefined;
  showUserForm.value = true;
}

function setUserForm() {
  const userLogin: string = route.params.userLogin as string;
  if (userLogin) {
    currentSelectedUser.value = userLogin;
    showUserForm.value = true;
  } else {
    showUserForm.value = false;
  }
}

fetchData();

initLeavePageStore();

watch(route, setUserForm);

onMounted(setUserForm);

onUnmounted(() => {
  confirmLeavePageStore.reset();
});
</script>

<template>
  <page-component
    :breadcrumbs="[
      {
        title: businessUnit
          ? t('page.businessUnits.title', {
              name: businessUnit?.name,
            })
          : '',
      },
    ]"
    :back-default-route="PATH_BUSINESS_UNITS"
    :is-loading="isLoading"
    :error="error"
  >
    <template #top-right>
      <lxc-button
        v-if="showUserAdd"
        :icon="ILxcPlus"
        :title="t('user.addUser.button')"
        class="self-center"
        html-type="button"
        type="primary"
        @click="onAddUser"
      >
        {{ t("user.addUser.button") }}
      </lxc-button>

      <lxc-button
        v-if="showFirmwareAdd"
        :icon="ILxcPlus"
        :title="t('firmware.newFirmware')"
        @click="showFirmwareForm = true"
      >
        {{ t("firmware.newFirmware") }}
      </lxc-button>
    </template>

    <tabs-with-navigation
      ref="tabsWithNavigationRef"
      :tab-navigations="tabNavigations"
      @update:selected-tab-index="
        (newSelectedTabIndex) => (selectedTabIndex = newSelectedTabIndex)
      "
    >
      <template #[descriptionTabId]>
        <business-unit-description
          v-if="businessUnit"
          :model-value="businessUnit!"
        />
      </template>

      <template #[relationsTabId]>
        <relations-component :business-unit-code="businessUnitCode" />
      </template>

      <template #[usersTabId]>
        <business-unit-users
          v-model:show-user-form="showUserForm"
          v-model:current-selected-user="currentSelectedUser"
          :path-context="usersTabRedirectPath"
          :business-unit-code="businessUnitCode"
        />
      </template>

      <template #[firmwaresTabId]>
        <business-unit-firmware
          v-model:show-firmware-form="showFirmwareForm"
          :business-unit-code="businessUnitCode"
        />
      </template>

      <template #[logsTabId]>
        <business-unit-logs
          :business-unit-code="businessUnitCode"
          :path-context="usersTabRedirectPath"
        />
      </template>
    </tabs-with-navigation>
  </page-component>
</template>
